import React from "react";
import { Link2, ExternalLink } from "lucide-react";
import { MD_LINK, BARE_URL } from "@/lib/richtext";

// Matches working paper codes like A, A03, A1a, C10, D2, F0, G1 when they exist in the current file.
const CODE_RE = /\b([A-G](?:0[0-9]|[0-9]{1,2}[a-z]?|Z)?)\b/g;

function isValidMatch(text, index, code, codes) {
    if (!codes.has(code)) return false;
    const before = text[index - 1] || " ";
    const after = text[index + code.length] || " ";
    if (code.length === 1 && !(/[\s(,;:/\-]/.test(before) && /[\s,;:.)\/\-]/.test(after))) return false;
    if (code.length === 1 && /^[A-G]$/.test(code) && /^[a-z]/.test(after)) return false;
    return true;
}

// Returns unique [{code, id}] for every referenced workpaper that exists in this file.
export function extractWpRefs(text, wps = []) {
    if (!text) return [];
    const codes = new Set(wps.map((w) => w.code));
    const byCode = Object.fromEntries(wps.map((w) => [w.code, w.id]));
    const seen = new Set();
    const out = [];
    let m;
    CODE_RE.lastIndex = 0;
    while ((m = CODE_RE.exec(text))) {
        const code = m[1];
        if (!isValidMatch(text, m.index, code, codes) || seen.has(code)) continue;
        seen.add(code);
        out.push({ code, id: byCode[code] });
    }
    return out;
}

const extLinkInline = (href, label, k) => (
    <a key={`x${k}`} href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
        className="text-brand underline decoration-dotted underline-offset-2 hover:decoration-solid break-words" data-testid="rich-link">{label}</a>
);

const extLinkPill = (href, label, k) => (
    <a key={`x${k}`} href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 align-middle text-xs font-semibold text-brand border border-brand/30 bg-brand/5 hover:bg-brand/10 rounded-sm px-1.5 py-0.5 mx-0.5 transition-colors" data-testid="rich-link">
        <Link2 className="w-3 h-3 shrink-0" /><span className="truncate max-w-[220px]">{label}</span><ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
    </a>
);

// Renders workpaper-code links within a plain-text segment (no URLs/markdown here).
function codeNodes(text, codes, byCode, engagementId, ctr) {
    const parts = [];
    let last = 0, m;
    CODE_RE.lastIndex = 0;
    while ((m = CODE_RE.exec(text))) {
        const code = m[1];
        if (!isValidMatch(text, m.index, code, codes)) continue;
        if (m.index > last) parts.push(text.slice(last, m.index));
        parts.push(
            <a key={`c${ctr.n++}`} href={`/engagements/${engagementId}/wp/${byCode[code]}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                className="font-num font-semibold text-brand underline decoration-dotted underline-offset-2 hover:decoration-solid" data-testid={`wp-ref-${code}`}>{code}</a>
        );
        last = m.index + code.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

// Splits a plain-text segment on bare URLs, rendering them as external links.
function urlNodes(text, codes, byCode, engagementId, ctr, ext) {
    const parts = [];
    let last = 0, m;
    BARE_URL.lastIndex = 0;
    while ((m = BARE_URL.exec(text))) {
        if (m.index > last) parts.push(...codeNodes(text.slice(last, m.index), codes, byCode, engagementId, ctr));
        parts.push(ext(m[1], m[1], ctr.n++));
        last = m.index + m[1].length;
    }
    if (last < text.length) parts.push(...codeNodes(text.slice(last), codes, byCode, engagementId, ctr));
    return parts;
}

export function WpRefs({ text, wps = [], engagementId, className = "", linkStyle = "inline" }) {
    if (!text) return null;
    const ext = linkStyle === "pill" ? extLinkPill : extLinkInline;
    const codes = new Set(wps.map((w) => w.code));
    const byCode = Object.fromEntries(wps.map((w) => [w.code, w.id]));
    const ctr = { n: 0 };
    const out = [];
    let last = 0, m;
    MD_LINK.lastIndex = 0;
    while ((m = MD_LINK.exec(text))) {
        if (m.index > last) out.push(...urlNodes(text.slice(last, m.index), codes, byCode, engagementId, ctr, ext));
        out.push(ext(m[2], m[1], ctr.n++));
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push(...urlNodes(text.slice(last), codes, byCode, engagementId, ctr, ext));
    return <span className={className}>{out}</span>;
}
